/**
 * Chart theme — token-derived colours and shared axis/grid/tooltip config.
 *
 * ── What was actually missing ──────────────────────────────────────────────
 * Not a charting library: `recharts` is already a dependency and is used in
 * seven files. Not a categorical palette either: src/lib/itemCategoryColors.ts
 * already owns the item-category encoding, and this module re-exports it rather
 * than forking a second one.
 *
 * What was missing is a THEME. Five of those seven files never read the theme
 * at all, and axis, grid and tooltip styling is re-decided per file in hard
 * hex — so charts do not follow light/dark, and two charts on the same page can
 * disagree about what a gridline looks like.
 *
 * ── Why the tokens are resolved rather than passed through ─────────────────
 * recharts writes most colours to SVG presentation attributes. `var(--green)`
 * does resolve in current browsers there, but not reliably across the
 * serialisation paths this app uses (PDF export renders through a detached
 * document where the custom property is not in scope). Resolving to a concrete
 * value at render time works everywhere and costs one getComputedStyle call.
 */
import { CATEGORY_HEX, CATEGORY_HEX_FALLBACK } from '../../lib/itemCategoryColors';
import type { Tone } from './foundation';

export { CATEGORY_HEX, CATEGORY_HEX_FALLBACK };

/** Fallbacks are the DARK palette: used only when no computed style is
    available (SSR, a detached document), where the app renders dark. */
const FALLBACK: Record<string, string> = {
  '--slate': '#3D4B6F',
  '--mustard': '#D4A017',
  '--coral': '#C0533A',
  '--green': '#3A7D56',
  '--rule': '#1A2234',
  '--rule-bright': '#222D40',
  '--surface': '#111827',
  '--bg-raised': '#0D1220',
  '--text-pri': '#D8DDE8',
  '--text-sec': '#9BA5BC',
  '--text-ter': '#8A96AB',
  '--text-dim': '#7C8AA3',
};

/**
 * Resolve one CSS custom property to a concrete colour.
 *
 * @param el element to resolve against — defaults to <html>. Pass the chart's
 *           own container when it may sit inside a locally themed subtree.
 */
export function resolveToken(token: string, el?: Element | null): string {
  const fallback = FALLBACK[token] ?? 'currentColor';
  if (typeof window === 'undefined' || typeof getComputedStyle !== 'function') return fallback;
  const target = el ?? document.documentElement;
  if (!target) return fallback;
  const value = getComputedStyle(target).getPropertyValue(token).trim();
  return value || fallback;
}

const TONE_TOKEN: Record<Tone, string> = {
  slate: '--slate',
  mustard: '--mustard',
  coral: '--coral',
  green: '--green',
  neutral: '--text-dim',
};

/** A semantic tone as a concrete colour for a chart series. */
export function toneColor(tone: Tone, el?: Element | null): string {
  return resolveToken(TONE_TOKEN[tone], el);
}

/**
 * Series order for charts whose series carry no domain meaning.
 *
 * Deliberately short. A chart needing a sixth colour is a chart that has
 * stopped being readable — group the tail into "Other" instead of extending
 * this. Where the series DO have domain meaning (item categories), use
 * CATEGORY_HEX, not this.
 */
export const SERIES_TONES: Tone[] = ['slate', 'mustard', 'green', 'coral', 'neutral'];

export function seriesColor(index: number, el?: Element | null): string {
  return toneColor(SERIES_TONES[index % SERIES_TONES.length], el);
}

/**
 * Shared recharts props.
 *
 * The register these encode comes from the investor deck: hairline axes, no
 * gridlines competing with the data, one accent, and the value emphasised at
 * the point the reader is meant to land on.
 */
export function chartTheme(el?: Element | null) {
  const rule = resolveToken('--rule', el);
  const ruleBright = resolveToken('--rule-bright', el);
  const textTer = resolveToken('--text-ter', el);
  const textPri = resolveToken('--text-pri', el);
  const surface = resolveToken('--bg-raised', el);

  return {
    /** Spread onto <XAxis> / <YAxis>. */
    axis: {
      stroke: ruleBright,
      tick: { fill: textTer, fontSize: 11, fontFamily: 'JetBrains Mono, monospace' },
      tickLine: false,
      axisLine: { stroke: ruleBright },
    },
    /**
     * Spread onto <CartesianGrid>. Horizontal only: vertical gridlines on a
     * time axis add lines the reader has to look past to see the shape.
     */
    grid: {
      stroke: rule,
      strokeDasharray: '0',
      vertical: false,
    },
    /** Spread onto <Tooltip>. */
    tooltip: {
      contentStyle: {
        background: surface,
        border: `1px solid ${ruleBright}`,
        borderRadius: 0,
        fontSize: 12,
        color: textPri,
      },
      labelStyle: {
        color: textTer,
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: 11,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.08em',
      },
      cursor: { fill: resolveToken('--hover-tint', el) },
    },
    /** Spread onto <Legend>. */
    legend: {
      wrapperStyle: {
        fontSize: 11,
        fontFamily: 'JetBrains Mono, monospace',
        color: textTer,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.08em',
      },
    },
  };
}
